import { countPairedCompositeCriteriaAgreements } from "@/metrics/agreement"
import { isBinaryMetric } from "@/metrics/metric-display"

import type { Annotation } from "@/types/api/Annotation"
import { MetricErrorStatus } from "@/types/api/MetricOutput"
import type { MetricOutputForAnnotation } from "@/types/api/MetricOutputsByMetric"
import type { MetricRecordData } from "@/types/metrics/contracts"

import type { AnnotationMachineComparison } from "../optimization/optimization"

const KNOWN_OUTPUT_TYPES = new Set(["float", "string", "set"])
const BINARY_LABELS = new Set(["yes", "no", "unknown"])

const normalizeText = (value: string | null | undefined): string | null => {
  const normalized = value?.trim().toLowerCase()
  return normalized || null
}

const normalizeBinary = ({
  floatValue,
  stringValue,
}: {
  floatValue: number | null | undefined
  stringValue: string | null | undefined
}): string | null => {
  const normalizedString = stringValue?.trim().toLowerCase()
  if (normalizedString && BINARY_LABELS.has(normalizedString)) {
    return normalizedString
  }
  if (typeof floatValue !== "number" || !Number.isFinite(floatValue)) {
    return null
  }
  if (floatValue === 1) {
    return "yes"
  }
  if (floatValue === 0) {
    return "no"
  }
  return "unknown"
}

const normalizeSet = (value: string[] | null | undefined): string[] | null => {
  if (!value?.length || !value.every((item) => typeof item === "string")) {
    return null
  }
  return [...value].sort()
}

const setsMatch = (first: string[], second: string[]): boolean =>
  first.length === second.length &&
  first.every((value, index) => value === second[index])

function compareScalarValues(
  annotation: Annotation,
  metricOutput: MetricOutputForAnnotation,
  metric: MetricRecordData,
): boolean | null {
  if (isBinaryMetric(metric)) {
    const human = normalizeBinary({
      floatValue: annotation.ground_truth_float_value,
      stringValue: annotation.ground_truth_string_value,
    })
    const machine = normalizeBinary({
      floatValue: metricOutput.float_value,
      stringValue: metricOutput.string_value,
    })
    return human == null || machine == null ? null : human === machine
  }

  const inferOutputType = !KNOWN_OUTPUT_TYPES.has(metricOutput.output_type)
  if (
    metricOutput.output_type === "float" ||
    (inferOutputType &&
      annotation.ground_truth_float_value != null &&
      metricOutput.float_value != null)
  ) {
    const human = annotation.ground_truth_float_value
    const machine = metricOutput.float_value
    return human == null ||
      machine == null ||
      !Number.isFinite(human) ||
      !Number.isFinite(machine)
      ? null
      : human === machine
  }

  if (
    metricOutput.output_type === "string" ||
    (inferOutputType &&
      annotation.ground_truth_string_value != null &&
      metricOutput.string_value != null)
  ) {
    const human = normalizeText(annotation.ground_truth_string_value)
    const machine = normalizeText(metricOutput.string_value)
    return human == null || machine == null ? null : human === machine
  }

  if (
    metricOutput.output_type === "set" ||
    (inferOutputType &&
      annotation.ground_truth_set_value != null &&
      metricOutput.set_value != null)
  ) {
    const human = normalizeSet(annotation.ground_truth_set_value)
    const machine = normalizeSet(metricOutput.set_value)
    return human == null || machine == null ? null : setsMatch(human, machine)
  }

  return null
}

const formatArray = (value: string[] | null | undefined): string | null =>
  value?.length ? value.join(", ") : null

function getStructuredMachineCriteria(
  metricOutput: MetricOutputForAnnotation,
): string[] | null {
  for (const source of [metricOutput.result, metricOutput.result_json]) {
    const individualResults = source?.individual_results
    if (!Array.isArray(individualResults) || individualResults.length === 0) {
      continue
    }
    return individualResults.map((item) => {
      const result = (item as { result?: unknown } | null)?.result
      return typeof result === "string" ? result : "UNKNOWN"
    })
  }
  return metricOutput.set_value
}

const formatHumanValue = (
  annotation: Annotation,
  isComposite: boolean,
): string => {
  if (isComposite) {
    return formatArray(annotation.ground_truth_set_value) || "N/A"
  }
  return (
    annotation.ground_truth_string_value?.trim() ||
    (annotation.ground_truth_float_value != null
      ? String(annotation.ground_truth_float_value)
      : formatArray(annotation.ground_truth_set_value)) ||
    "N/A"
  )
}

const formatMachineValue = (
  metricOutput: MetricOutputForAnnotation | undefined,
  isComposite: boolean,
): string => {
  if (!metricOutput) {
    return "N/A"
  }
  if (isComposite) {
    return formatArray(getStructuredMachineCriteria(metricOutput)) || "N/A"
  }
  return (
    metricOutput.string_value?.trim() ||
    (metricOutput.float_value != null
      ? String(metricOutput.float_value)
      : formatArray(metricOutput.set_value)) ||
    "N/A"
  )
}

export function compareAnnotationWithMachine(
  annotation: Annotation,
  metricOutput: MetricOutputForAnnotation | undefined,
  metric: MetricRecordData,
): AnnotationMachineComparison {
  const isComposite = metric.metric_manager === "manager_composite_evaluation"
  const base = {
    humanValue: formatHumanValue(annotation, isComposite),
    machineValue: formatMachineValue(metricOutput, isComposite),
  }
  if (
    !metricOutput ||
    (metricOutput.error_status != null &&
      metricOutput.error_status !== MetricErrorStatus.COMPLETED)
  ) {
    return {
      ...base,
      status: "not_comparable",
      matchingUnits: 0,
      comparableUnits: 0,
    }
  }

  if (isComposite) {
    const outcome = countPairedCompositeCriteriaAgreements(metricOutput, {
      set_value: annotation.ground_truth_set_value,
      json_value: annotation.ground_truth_json,
    })
    if (!outcome || outcome.total === 0) {
      return {
        ...base,
        status: "not_comparable",
        matchingUnits: 0,
        comparableUnits: 0,
      }
    }
    return {
      ...base,
      status: outcome.matching === outcome.total ? "agreement" : "disagreement",
      matchingUnits: outcome.matching,
      comparableUnits: outcome.total,
    }
  }

  const agrees = compareScalarValues(annotation, metricOutput, metric)
  if (agrees == null) {
    return {
      ...base,
      status: "not_comparable",
      matchingUnits: 0,
      comparableUnits: 0,
    }
  }
  return {
    ...base,
    status: agrees ? "agreement" : "disagreement",
    matchingUnits: agrees ? 1 : 0,
    comparableUnits: 1,
  }
}
